/**
 * The campaign's half of the command vocabulary.
 *
 * `applyCommand` delegates the whole 40–53 block here, which keeps the strategic
 * layer's write-door in the strategic layer while still going through the one
 * door the rest of the project uses. The world-map UI issues these and nothing
 * else; it never touches campaign state directly, exactly as the settlement HUD
 * never touches a building.
 *
 * Everything below refuses politely rather than throwing. A command that has
 * become illegal between the click and the tick — the army marched, the battle
 * resolved, the province changed hands — is an ordinary event, not an error.
 */

import { Cmd, type Command } from '../commands.ts';
import type { World } from '../world.ts';
import { recruitArmyHero } from './heroes.ts';
import {
  ArmyStance,
  BOUNTY_GOLD,
  MAX_ARMY_POINTS,
  MIN_ARMY_POINTS,
  type CampaignState,
} from './types.ts';
import { haltMarch, orderIntercept, roadName } from './marches.ts';
import {
  beginWorks,
  cancelWorks,
  developCost,
  MAX_FORT_BASE,
  raiseWalls,
  tierName,
} from './holdings.ts';
import { tryPostBounty } from './bandits.ts';
import { MAX_BARRACKS_POINTS, sallyArmy, shelterArmy, shelterRefusal } from './garrisons.ts';
import { MAX_AGE } from '../../data/ages.ts';
import { MerchantMission } from './tradetypes.ts';
import { nodeName } from './trade.ts';
import { merchantAt, postMerchant, recallMerchant } from './merchants.ts';
import {
  disbandFleet,
  disembarkFleet,
  embarkArmy,
  findFleet,
  haltVoyage,
  orderFleetIntercept,
  orderSail,
  sailMonths,
  tryAddHulls,
  tryBuildPort,
  tryRaiseFleet,
} from './fleets.ts';
import {
  armyModels,
  findArmy,
  mergeArmies,
  orderMarch,
  removeArmy,
  setSiegeStance,
  tryRaiseArmy,
  tryReinforceArmy,
} from './armies.ts';
import { logLine, nationName, provinceName } from './state.ts';
import { RESOURCE_KEYS, RESOURCE_LABELS } from '../types.ts';
import { OrderKind } from './markettypes.ts';
import { cancelOrder, findOrder, tryPostBuy, tryPostSell } from './market.ts';
import { advance, endTurn } from './turn.ts';
import { answerRealmEvent } from './chronicleturn.ts';
import { autoResolveBattle, resolveFoughtBattle } from './conquest.ts';
import { provinceWorth } from './diplomacy.ts';
import { CasusBelli } from './diplotypes.ts';
import { casusBelliAgainst, declareWar, makePeace, warById } from './war.ts';
import { evaluatePlayerPeace, peaceTaker } from './diploturn.ts';
import {
  annexSubject,
  answerOffer,
  breakAllianceWith,
  demandVassalage,
  DiploAction,
  grantPassage,
  proposeAlliance,
  proposeMarriage,
  releaseSubject,
  requestPassage,
  revokePassage,
} from './diploacts.ts';

/**
 * The things you can tell a fleet to do that are not "sail there".
 *
 * Carried in `b` of `Cmd.CampaignFleetAction`. See the note on the command
 * block in `commands.ts` for why these share a door.
 */
export const FleetAction = {
  /** Put every host aboard onto the beach where the hulls lie. */
  Disembark: 0,
  /** Pay off the hulls. Anything aboard is landed first. */
  Disband: 1,
  /** Put about. The lane reverses; the sea already covered is still to cover. */
  Halt: 2,
} as const;
export type FleetActionId = (typeof FleetAction)[keyof typeof FleetAction];

/**
 * The blocks beyond the first range, and the one predicate that knows them.
 *
 * 40–62 is contiguous and full at both ends: 63 is diplomacy's spare and 39 is
 * the settlement's neighbour. Everything between 63 and 92 is spoken for by
 * somebody else — the debug console at 64–69, the cart at 71–76, the handles
 * block at 77–82, the curtain at 87–92 — so widening `CAMPAIGN_CMD_LAST` to
 * reach trade at 93 or holdings at 105 would silently route every one of them
 * into this file to be ignored.
 *
 * So there are **four** ranges and exactly one predicate. `applyCommand` asks
 * the predicate; nothing outside this file needs to know how many ranges there
 * are, which is what made adding the third and fourth a two-line change each.
 *
 * Four branches of this batch each arrived with a "second range" of their own
 * and each wrote the paragraph above in their own words — trade at 93, the sea
 * at 99, holdings at 105. Not one of the *numbers* collided, because they were
 * declared in `COORDINATION.md` before they were taken. Only the plumbing did,
 * and unifying four predicates into one was minutes. That is the whole argument
 * for the reservation table.
 */
export const CAMPAIGN_CMD_FIRST = Cmd.CampaignEndTurn;
export const CAMPAIGN_CMD_LAST = Cmd.CampaignAnswerOffer;
export const CAMPAIGN_TRADE_CMD_FIRST = Cmd.CampaignTradeCollect;
export const CAMPAIGN_TRADE_CMD_LAST = 98;
export const CAMPAIGN_SEA_CMD_FIRST = Cmd.CampaignBuildPort;
export const CAMPAIGN_SEA_CMD_LAST = Cmd.CampaignFleetIntercept;
export const CAMPAIGN_HOLD_CMD_FIRST = Cmd.CampaignDevelop;
export const CAMPAIGN_HOLD_CMD_LAST = 108;
/**
 * The fifth, and the two-line change the paragraph above promised.
 *
 * 121–126, released unused by the passage work and taken here. It could not
 * simply widen the holdings range: 109–120 between them belong to the races
 * block, and reaching 121 from 108 would have routed `PickKingdom` and
 * `DebugSetRace` into this file to be silently ignored — which is the exact
 * failure the note above says the four-range shape exists to prevent.
 */
export const CAMPAIGN_EVENT_CMD_FIRST = Cmd.CampaignAnswerEvent;
export const CAMPAIGN_EVENT_CMD_LAST = 126;
/**
 * The seventh: the free market's 133–138. Three taken, 136–138 reserved to the
 * block, and the range runs to 138 rather than 135 for the same reason trade's
 * runs to 98 — a verb added to the block later should route here the day it is
 * numbered, not nowhere. 127–132 sit between the chronicle's spares and this
 * block and belong to nobody yet; widening the event range across them would
 * silently swallow whatever claims them next.
 */
export const CAMPAIGN_MARKET_CMD_FIRST = Cmd.MarketPostSell;
export const CAMPAIGN_MARKET_CMD_LAST = 138;
/**
 * The eighth: standing garrisons and hiding in the walls, 164–169.
 *
 * A new range rather than a widening, for the reason the note above gives twice
 * already: 139–163 belong to five other blocks — the depletion block at 140–145,
 * the field at 146–151, and three branches of this same batch at 156–163 — and
 * reaching 164 from 138 would silently route every one of them into this file
 * to be ignored. The range runs to 169 rather than 166 so that a verb added to
 * the block later routes here the day it is numbered, not nowhere.
 */
export const CAMPAIGN_GARRISON_CMD_FIRST = Cmd.CampaignShelterArmy;
export const CAMPAIGN_GARRISON_CMD_LAST = 169;
/**
 * The ninth: the door into a holding, 200–201.
 *
 * One number wide in practice and declared as a range for the reason every note
 * above gives — a verb added to the towns block later should route here the day
 * it is numbered rather than nowhere. It stops at 201 rather than running to
 * 207 because **201 is deliberately not a campaign command**: `Cmd.WreckTown`
 * is applied to the *settlement* being broken, not to the realm, and a range
 * that swallowed it would route it into this file to be silently ignored — the
 * exact failure the four-range shape was invented to prevent, one number away
 * from the note that says so.
 */
export const CAMPAIGN_TOWN_CMD_FIRST = Cmd.EnterHold;
export const CAMPAIGN_TOWN_CMD_LAST = Cmd.EnterHold;

/**
 * The tenth: the board's own controls, 208–213.
 *
 * A new range and not a widening for the third time in this file's history, and
 * the reason has not changed: 170–207 belong to five other blocks and to three
 * branches of this same batch, and reaching 208 from 169 would silently route
 * every one of them here to be ignored. The range runs to 213 rather than 208
 * so that a verb added to the block later routes here the day it is numbered.
 */
export const CAMPAIGN_BOARD_CMD_FIRST = Cmd.CampaignMarchGroup;
export const CAMPAIGN_BOARD_CMD_LAST = 213;

export function isCampaignCommand(k: number): boolean {
  return (
    (k >= CAMPAIGN_CMD_FIRST && k <= CAMPAIGN_CMD_LAST) ||
    (k >= CAMPAIGN_TRADE_CMD_FIRST && k <= CAMPAIGN_TRADE_CMD_LAST) ||
    (k >= CAMPAIGN_SEA_CMD_FIRST && k <= CAMPAIGN_SEA_CMD_LAST) ||
    (k >= CAMPAIGN_HOLD_CMD_FIRST && k <= CAMPAIGN_HOLD_CMD_LAST) ||
    (k >= CAMPAIGN_EVENT_CMD_FIRST && k <= CAMPAIGN_EVENT_CMD_LAST) ||
    (k >= CAMPAIGN_MARKET_CMD_FIRST && k <= CAMPAIGN_MARKET_CMD_LAST) ||
    (k >= CAMPAIGN_GARRISON_CMD_FIRST && k <= CAMPAIGN_GARRISON_CMD_LAST) ||
    (k >= CAMPAIGN_TOWN_CMD_FIRST && k <= CAMPAIGN_TOWN_CMD_LAST) ||
    (k >= CAMPAIGN_BOARD_CMD_FIRST && k <= CAMPAIGN_BOARD_CMD_LAST) ||
    k === Cmd.CampaignRecruitHero
  );
}

export function applyCampaignCommand(w: World, cmd: Command): void {
  const c = w.campaign;
  if (!c) return;

  /**
   * Whose order this is.
   *
   * Every handler below used to read `c.playerNation` — "the crown this client
   * is playing" — which is the right answer for exactly as long as there is one
   * player. In a room there are several, one simulation, and `playerNation` is
   * the one field that deliberately differs between machines: a command from
   * another seat applied through it raised *my* army with *my* treasury and put
   * it on the board twice over.
   *
   * So an order carries its crown. `cmd.n` is set by whoever received the
   * order from a player — the server, for a room — and left unset by the UI, by
   * the bots and by every replay, where "the player" is the only answer there
   * has ever been. Ownership is then a fact about the order rather than about
   * the machine applying it, which is what lets one realm take orders from four
   * crowns without asking whose screen it is on.
   */
  const me = cmd.n ?? c.playerNation;

  // Battle results are the one thing that is legal mid-turn; that is the whole
  // point of the pending record.
  if (cmd.k === Cmd.CampaignResolveBattle) {
    if (!c.pending) return;
    // A verdict is only a verdict from somebody who was in the fight. Two
    // crowns on one field both report, each phrasing it as their own win, and
    // the second report would otherwise overturn the first.
    if (!inPending(c, me)) return;
    resolveFoughtBattle(w, me, cmd.a > 0, permille(cmd.b), permille(cmd.c), cmd.d === 1);
    advance(w);
    return;
  }
  if (cmd.k === Cmd.CampaignAutoResolve) {
    if (!c.pending) return;
    if (!inPending(c, me)) return;
    autoResolveBattle(w);
    advance(w);
    return;
  }

  if (c.over) return;
  /**
   * Everything else is an order, and orders are now given whenever the player
   * likes.
   *
   * This used to read `c.phase !== TurnPhase.Orders || c.pending`, which was
   * right when the only way out of `Orders` was a button the player had just
   * pressed themselves. It is wrong now: the council sits on its own, one phase
   * per tick, and a click that happened to land in that fifth of a second would
   * be swallowed with nothing on screen to explain it. A dropped order is a far
   * worse bug than an order given while the sieges are being counted — every
   * phase function reads the board fresh each time it is called, so an army
   * raised, marched or stood down between two of them is simply a thing that
   * happened this month.
   *
   * `pending` still refuses everything, and has to: there is a battle on the
   * table that somebody has to answer for before the board means anything.
   */
  if (c.pending) return;

  /**
   * The answer to the realm's chronicle, and then the gate it gets through.
   *
   * Same shape as the battle result above and same argument: it is the one thing
   * that is legal while the realm is suspended, because it is the thing the
   * suspension is waiting for. `s` names the event so a click from a modal that
   * has already been dismissed cannot answer the question currently on the table.
   */
  if (cmd.k === Cmd.CampaignAnswerEvent) {
    // The question was put to one crown. In a room the other seats can see it
    // — the realm is frozen in front of all of them — and none of them may
    // answer it: the choice is paid for out of that crown's treasury.
    if (c.pendingEvent && c.pendingEvent.ctx.nation !== me) return;
    answerRealmEvent(c, w.rngCampaign, cmd.s, cmd.a);
    return;
  }
  /**
   * Everything else waits, and this is the mirror of `apply.ts:119` — the gate
   * that refuses settlement orders while the town has a Chronicle question up.
   *
   * It has to be enforced here rather than in the UI. `stepRealm` already refuses
   * to advance the month; if orders still went through, a click queued behind the
   * modal would land in a frozen realm, and a replay whose modal happened to be
   * dismissed a frame earlier would apply it to a different board.
   */
  if (c.pendingEvent) return;

  switch (cmd.k) {
    case Cmd.CampaignRecruitHero:
      recruitArmyHero(w, cmd.a, cmd.s, me);
      break;
    case Cmd.CampaignEndTurn:
      endTurn(w);
      break;
    case Cmd.CampaignRaiseArmy:
      doRaise(w, c, me, cmd.a, cmd.b);
      break;
    case Cmd.CampaignMoveArmy:
      doMove(c, me, cmd.a, cmd.b);
      break;
    case Cmd.CampaignMarchGroup:
      doMarchGroup(c, me, cmd.a, cmd.s);
      break;
    case Cmd.CampaignMergeArmies:
      doMerge(c, me, cmd.a, cmd.b);
      break;
    case Cmd.CampaignDisbandArmy:
      doDisband(c, me, cmd.a);
      break;
    case Cmd.CampaignBesiege:
      doStance(c, me, cmd.a, ArmyStance.Besieging);
      break;
    case Cmd.CampaignShelterArmy:
      doShelter(c, me, cmd.a);
      break;
    case Cmd.CampaignSallyArmy:
      doSally(c, me, cmd.a);
      break;
    case Cmd.CampaignSetWatch:
      doSetWatch(c, cmd.a, cmd.b, cmd.c);
      break;
    case Cmd.EnterHold:
      doEnterHold(c, me, cmd.a);
      break;
    case Cmd.CampaignAssault:
      doStance(c, me, cmd.a, ArmyStance.Assaulting);
      break;
    case Cmd.CampaignFortify:
      doFortify(c, me, cmd.a);
      break;
    case Cmd.CampaignReinforce:
      doReinforce(w, c, me, cmd.a, cmd.b);
      break;
    case Cmd.CampaignIntercept:
      doIntercept(c, me, cmd.a, cmd.b);
      break;
    case Cmd.CampaignHalt:
      doHalt(c, me, cmd.a);
      break;
    case Cmd.CampaignBounty:
      doBounty(c, me, cmd.a);
      break;
    case Cmd.CampaignDeclareWar:
      doDeclareWar(c, me, cmd.a, cmd.b);
      break;
    case Cmd.CampaignOfferPeace:
      doOfferPeace(c, me, cmd.a, cmd.b, cmd.c);
      break;
    case Cmd.CampaignDiplomacy:
      doDiplomacy(c, me, cmd.a, cmd.b);
      break;
    case Cmd.CampaignAnswerOffer:
      doAnswerOffer(c, me, cmd.a, cmd.b > 0);
      break;
    case Cmd.CampaignTradeCollect:
      doPostMerchant(c, me, cmd.a, MerchantMission.Collect, -1);
      break;
    case Cmd.CampaignTradeSteer:
      doPostMerchant(c, me, cmd.a, MerchantMission.Steer, cmd.b);
      break;
    case Cmd.CampaignTradeRecall:
      doRecallMerchant(c, me, cmd.a);
      break;
    case Cmd.CampaignDevelop:
      doDevelop(c, me, cmd.a);
      break;
    case Cmd.CampaignCancelWorks:
      doCancelWorks(c, me, cmd.a);
      break;
    case Cmd.CampaignBuildPort:
      doBuildPort(c, me, cmd.a);
      break;
    case Cmd.CampaignRaiseFleet:
      doRaiseFleet(c, me, cmd.a, cmd.b, cmd.c);
      break;
    case Cmd.CampaignSailFleet:
      doSail(c, me, cmd.a, cmd.b);
      break;
    case Cmd.CampaignEmbark:
      doEmbark(c, me, cmd.a, cmd.b);
      break;
    case Cmd.CampaignFleetAction:
      doFleetAction(c, me, cmd.a, cmd.b);
      break;
    case Cmd.MarketPostSell:
      doPostOrder(c, me, OrderKind.Sell, cmd.a, cmd.b, cmd.c);
      break;
    case Cmd.MarketPostBuy:
      doPostOrder(c, me, OrderKind.Buy, cmd.a, cmd.b, cmd.c);
      break;
    case Cmd.MarketCancel:
      doCancelOrder(c, me, cmd.a);
      break;
    case Cmd.CampaignFleetIntercept:
      doFleetIntercept(c, me, cmd.a, cmd.b);
      break;
    default:
      break;
  }
}

// ── Diplomacy ──────────────────────────────────────────────────────────────

/**
 * The player's half of the diplomatic vocabulary.
 *
 * Every one of these refuses politely, like everything else in this file — a
 * truce that came into force between the click and the tick, an ally that has
 * already been called, a war that somebody else ended. The world map is expected
 * to grey the button using the matching `…Reason` function, which returns a
 * sentence; these are the second line of defence rather than the first.
 */
function doDeclareWar(c: CampaignState, me: number, target: number, cb: number): void {
  if (me === target) return;
  const reasons = casusBelliAgainst(c, me, target);
  // A pretext you do not have is no pretext at all. Falling back rather than
  // refusing, because the click was still a click on "declare war".
  const chosen = reasons.includes(cb) ? cb : CasusBelli.None;
  declareWar(c, me, target, chosen, richestProvinceOf(c, target));
}

function richestProvinceOf(c: CampaignState, nation: number): number {
  let best = -1;
  let bestWorth = -1;
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== nation) continue;
    const worth = provinceWorth(c, p);
    if (worth > bestWorth) {
      bestWorth = worth;
      best = p;
    }
  }
  return best;
}

/**
 * Put terms to somebody. Which way round the terms run is read off the war score
 * rather than declared by the command: if the player can afford the demand they
 * are demanding it, and if they cannot then they are offering it. That keeps one
 * command for both halves of a negotiation and, more usefully, makes it
 * impossible to accidentally demand something while losing.
 */
function doOfferPeace(
  c: CampaignState,
  me: number,
  warId: number,
  term: number,
  province: number,
): void {
  const war = warById(c, warId);
  if (!war) return;
  if (war.attacker !== me && war.defender !== me) return;
  if (!evaluatePlayerPeace(c, war, term, province)) {
    logLine(c, 'The terms are refused. The war goes on.');
    return;
  }
  makePeace(c, war, peaceTaker(c, war, term, province), term, province);
}

function doDiplomacy(c: CampaignState, me: number, action: number, target: number): void {
  switch (action) {
    case DiploAction.ProposeAlliance:
      proposeAlliance(c, me, target);
      break;
    case DiploAction.BreakAlliance:
      breakAllianceWith(c, me, target);
      break;
    case DiploAction.ProposeMarriage:
      proposeMarriage(c, me, target);
      break;
    case DiploAction.DemandVassalage:
      demandVassalage(c, me, target);
      break;
    case DiploAction.AnnexSubject:
      annexSubject(c, me, target);
      break;
    case DiploAction.ReleaseSubject:
      releaseSubject(c, me, target);
      break;
    /**
     * The three passage verbs. `target` is the other crown in every case; which
     * end of the arrangement it is on is decided by which verb was used, not by
     * a direction flag, so a click that meant "let them in" can never be read as
     * "ask them to let me in".
     */
    case DiploAction.RequestPassage:
      requestPassage(c, me, target);
      break;
    case DiploAction.GrantPassage:
      grantPassage(c, me, target);
      break;
    case DiploAction.RevokePassage:
      revokePassage(c, me, target);
      break;
    default:
      break;
  }
}

/**
 * Answer something on your own table, and nobody else's.
 *
 * `answerOffer` takes the offer's word for who it is addressed to, which is the
 * right shape for a call the council makes on a crown's behalf. An order off a
 * wire is not that: in a room the offer id is a number any seat can name, and a
 * marriage proposed to one crown could be accepted by another. The addressee is
 * checked here rather than in `diploacts.ts` so the AI's own path is untouched.
 */
function doAnswerOffer(c: CampaignState, me: number, offerId: number, accept: boolean): void {
  const offer = c.diplo.offers.find((o) => o.id === offerId);
  if (!offer || offer.to !== me) return;
  answerOffer(c, offerId, accept);
}

/** Was this crown on the field whose result is on the table? */
function inPending(c: CampaignState, me: number): boolean {
  const p = c.pending;
  if (!p) return false;
  return p.attackerNation === me || p.defenderNation === me;
}

/** Battle mode reports survivors as a fraction; the command carries permille. */
function permille(v: number): number {
  return Math.max(0, Math.min(1, v / 1000));
}

function doRaise(w: World, c: CampaignState, me: number, province: number, points: number): void {
  const want = Math.max(MIN_ARMY_POINTS, Math.min(MAX_ARMY_POINTS, Math.round(points)));
  const a = tryRaiseArmy(c, w.rngCampaign, me, province, want);
  if (!a) return;
  logLine(c, `${a.name} musters at ${provinceName(province)} — ${armyModels(a)} models.`);
}

function doMove(c: CampaignState, me: number, armyId: number, to: number): void {
  const a = findArmy(c, armyId);
  if (!a || a.owner !== me) return;
  if (!orderMarch(c, a, to)) return;
  logLine(c, `${a.name} takes the road to ${provinceName(to)}.`);
}

/**
 * Several hosts, one destination, one line in the chronicle.
 *
 * Each host is ordered on its own terms through `orderMarch` — the same
 * function a single order goes through, so interception, closed borders, the
 * withdrawal escape and the drawn lane all behave exactly as they do for one
 * host. **Nothing is a formation**: they leave together and arrive whenever
 * their own roads let them.
 *
 * The ids are sorted before they are walked. The order cannot change the
 * outcome — every `orderMarch` here is independent of the others — but a
 * command whose effect depends on the order a *caller* happened to build a
 * string in is a command that can differ between two clients with the same
 * selection, and this file's whole job is that it cannot.
 *
 * A host that refuses is skipped rather than aborting the rest: three of your
 * four marching is what the player asked for as far as it can be given, and the
 * board says which one stayed behind in `orderRefusal`'s own words.
 */
function doMarchGroup(c: CampaignState, me: number, to: number, list: string): void {
  const ids = [
    ...new Set(
      list
        .split(',')
        .map((t) => Number(t))
        .filter((n) => Number.isInteger(n)),
    ),
  ].sort((a, b) => a - b);
  // A gesture selects hosts, and a crown has five. Anything past a sane bound
  // is a malformed order rather than an ambitious one.
  if (ids.length === 0 || ids.length > 64) return;
  let sent = 0;
  let last = '';
  for (const id of ids) {
    const a = findArmy(c, id);
    if (!a || a.owner !== me) continue;
    if (!orderMarch(c, a, to)) continue;
    sent++;
    last = a.name;
  }
  if (sent === 0) return;
  logLine(
    c,
    sent === 1
      ? `${last} takes the road to ${provinceName(to)}.`
      : `${sent} hosts take the road to ${provinceName(to)}.`,
  );
}

function doMerge(c: CampaignState, me: number, intoId: number, fromId: number): void {
  const into = findArmy(c, intoId);
  const from = findArmy(c, fromId);
  if (!into || !from) return;
  if (into.owner !== me || from.owner !== me) return;
  const name = from.name;
  if (mergeArmies(c, into, from)) logLine(c, `${name} folds into ${into.name}.`);
}

function doDisband(c: CampaignState, me: number, armyId: number): void {
  const a = findArmy(c, armyId);
  if (!a || a.owner !== me) return;
  // Half of them go home rather than into the ground, and can be called again.
  c.nations[a.owner].manpower += Math.floor(armyModels(a) * 0.5);
  logLine(c, `${a.name} is stood down at ${provinceName(a.at)}.`);
  removeArmy(c, armyId);
}

function doStance(c: CampaignState, me: number, armyId: number, stance: number): void {
  const a = findArmy(c, armyId);
  if (!a || a.owner !== me) return;
  if (!setSiegeStance(c, a, stance)) return;
  logLine(
    c,
    stance === ArmyStance.Assaulting
      ? `${a.name} will storm ${provinceName(a.at)} when the horns go.`
      : `${a.name} settles in outside ${provinceName(a.at)}.`,
  );
}

/**
 * Behind the gates, and back out of them.
 *
 * Both go through the same `*Refusal` reader the world map greys its button
 * with, so a click that has become illegal between the press and the tick —
 * the province changed hands, another host filled the walls, the order to march
 * landed first — is refused by the simulation in its own words rather than by
 * the UI having remembered to check twice.
 */
function doShelter(c: CampaignState, me: number, armyId: number): void {
  const a = findArmy(c, armyId);
  if (!a || a.owner !== me) return;
  const why = shelterRefusal(c, a);
  if (why) {
    // Only the player's own crown hears about it; an AI order that misses is
    // not news, and the chronicle is forty-eight lines long.
    if (me === c.playerNation) logLine(c, `${a.name} stays in the field. ${why}`);
    return;
  }
  if (!shelterArmy(c, a)) return;
  logLine(c, `${a.name} takes the walls of ${provinceName(a.at)}.`);
}

function doSally(c: CampaignState, me: number, armyId: number): void {
  const a = findArmy(c, armyId);
  if (!a || a.owner !== me) return;
  if (!sallyArmy(c, a)) return;
  logLine(c, `${a.name} comes back out of ${provinceName(a.at)} and forms up.`);
}

/**
 * What the player's city amounts to, written onto the realm.
 *
 * The province travels with the order for the same reason `SetCapitalStanding`
 * carries one: a room has a capital per crown, and "mine" is a different
 * province on every client, so reading it here would have each machine improve
 * a different city.
 *
 * The Age is clamped rather than trusted. It arrives from a settlement world
 * this file cannot see, and a garbage value would scale every standing garrison
 * on a six-hundred-province board.
 */
function doSetWatch(c: CampaignState, province: number, barracks: number, age: number): void {
  const prov = c.provinces[province];
  if (prov) prov.barracks = Math.max(0, Math.min(MAX_BARRACKS_POINTS, Math.round(barracks)));
  /**
   * The Age comes off the **seat** and nowhere else.
   *
   * Inert until this batch, because the capital was the only settlement world
   * that existed and therefore the only one that ever sent this. It is not
   * inert now: a crown may hold four towns, and this is a plain write, so a
   * player who walked into a freshly captured hamlet at the first Age would
   * have knocked the whole realm back to the first Age on the way out —
   * a strategic layer reset by opening a door. There is one Age on this board
   * (see `CampaignState.age`) and the seat is what it is measured at.
   */
  if (province === c.capital) c.age = Math.max(1, Math.min(MAX_AGE, Math.round(age)));
}

/**
 * The player stands in a holding of theirs as a place.
 *
 * Two writes and both are about what the *campaign* now knows: this ground has
 * a settlement world of yours on it, and whatever sack was owed to it has just
 * been paid. The world itself is built by `createHoldWorld`, which is a pure
 * read of this campaign — see the note on `Cmd.EnterHold`.
 *
 * `settled` is written **only when it is zero**. It is the turn this place was
 * first stood in, not the turn it was last visited: walking back into a town
 * you have held for two years must not restamp its founding, and a town you
 * lost and took back is still the town you built. The one thing a monotonic
 * `Math.max` would buy — never going backwards — is bought here by never
 * writing twice.
 */
function doEnterHold(c: CampaignState, me: number, province: number): void {
  const prov = c.provinces[province];
  if (!prov) return;
  // Somebody else's ground is not a door. Read off the order's own crown
  // rather than off `playerNation`, for the reason every handler in this file
  // does: in a room, "the player" is a different answer on every machine.
  if (prov.owner !== me) return;
  if (prov.settled === 0) prov.settled = Math.max(1, c.turn);
  prov.wreck = 0;
}

/**
 * Raise a province's walls. Expensive, slow to pay back, and — until the tier
 * ladder — the only thing on the map that made ground you already hold harder
 * to take off you.
 *
 * The rule and the price live in `holdings.ts` with the rest of the works, since
 * walls are now supplied out of a yard like everything else built of stone. The
 * constants are re-exported here because this is where callers have always
 * looked for them.
 */
export { FORTIFY_GOLD, FORTIFY_STONE } from './holdings.ts';
/**
 * The fortification ceiling everywhere except a citadel.
 *
 * Kept as a constant because the world map and several tests read it, and it is
 * still the right answer for every province on the board bar one kind. The
 * per-province ceiling is `maxFortAt`, which returns 4 on a tenth-tier holding;
 * anything asking "can these walls go higher" should ask that instead.
 */
export const MAX_FORT = MAX_FORT_BASE;

function doFortify(c: CampaignState, me: number, province: number): void {
  if (province < 0 || province >= c.provinces.length) return;
  if (!raiseWalls(c, province, me)) return;
  logLine(
    c,
    `${provinceName(province)} raises its walls to ${c.provinces[province].fort}. ` +
      `${nationName(c, me)} intends to stay.`,
  );
}

/**
 * Send a host out after one that is on the road.
 *
 * The order is stored on the army and carried out in the coming turn's `Roads`
 * phase, before anybody moves. It is deliberately an order rather than an
 * immediate effect: the quarry may arrive, turn around or die first, and a chase
 * that finds nothing to chase is an ordinary event, not an error.
 */
function doIntercept(c: CampaignState, me: number, chaserId: number, targetId: number): void {
  const chaser = findArmy(c, chaserId);
  const target = findArmy(c, targetId);
  if (!chaser || !target) return;
  if (chaser.owner !== me) return;
  if (!orderIntercept(c, chaser, target)) return;
  logLine(
    c,
    `${chaser.name} goes out to meet ${target.name} on ` +
      `${roadName(c, target.moveFrom, target.moveTo)}.`,
  );
}

function doHalt(c: CampaignState, me: number, armyId: number): void {
  const a = findArmy(c, armyId);
  if (!a || a.owner !== me) return;
  const home = a.moveFrom;
  if (!haltMarch(a)) return;
  logLine(c, `${a.name} turns back toward ${provinceName(home)}.`);
}

/**
 * Pay to be rid of brigands you have no army to send at.
 *
 * Slower and, per point of camp, dearer than marching on one — but available on
 * turn one, which marching on one is not. It is the answer to the opening
 * position where a camp sits on your bread country and every coin you have is
 * needed for the first host.
 */
function doBounty(c: CampaignState, me: number, province: number): void {
  if (!tryPostBounty(c, me, province, BOUNTY_GOLD)) return;
  logLine(
    c,
    `A purse of ${BOUNTY_GOLD} is put up for the camp at ${provinceName(province)}. ` +
      `Somebody will want it.`,
  );
}

// ── Trade ──────────────────────────────────────────────────────────────────

/**
 * Post or retask a merchant. Refuses politely, like everything else here — the
 * node may have gone out of reach, the road may not exist, the last free
 * merchant may have been taken by the same click twice. `postReason` is what the
 * panel greys the button with, and this is the second line of defence.
 *
 * A merchant already standing in the node is retasked in place and travels no
 * further; that is `postMerchant`'s rule, not this one's, so the AI council gets
 * exactly the same deal.
 */
function doPostMerchant(
  c: CampaignState,
  me: number,
  node: number,
  mission: number,
  toward: number,
): void {
  const already = merchantAt(c, me, node);
  const m = postMerchant(c, me, node, mission, toward);
  if (!m) return;
  if (already) {
    logLine(
      c,
      mission === MerchantMission.Steer
        ? `Our factor at ${nodeName(c, node)} is told to turn the carts toward ${nodeName(c, toward)}.`
        : `Our factor at ${nodeName(c, node)} is told to open the ledger instead.`,
    );
    return;
  }
  logLine(
    c,
    `A factor sets out for ${nodeName(c, node)} — ${m.travel} ` +
      `${m.travel === 1 ? 'month' : 'months'} on the road.`,
  );
}

function doRecallMerchant(c: CampaignState, me: number, node: number): void {
  if (!recallMerchant(c, me, node)) return;
  logLine(c, `Our factor at ${nodeName(c, node)} is called home.`);
}

// ── The market ─────────────────────────────────────────────────────────────

/**
 * Put an order on the book. Refuses politely like everything else here —
 * `postRefusal` is the sentence the panel greys the button with, and this is
 * the second line of defence. The two gates every campaign order obeys
 * (`c.pending`, `c.pendingEvent`) sit above the switch and cover these cases
 * without a line being written for them.
 *
 * `keyIdx` indexes `RESOURCE_KEYS` — the command carries numbers, and that
 * table's order is frozen by the same rule the HUD strip leans on. `price` is
 * tenths of a gold per unit, straight off the wire.
 */
function doPostOrder(
  c: CampaignState,
  me: number,
  kind: number,
  keyIdx: number,
  amount: number,
  price: number,
): void {
  const key = RESOURCE_KEYS[keyIdx];
  if (!key) return;
  const o =
    kind === OrderKind.Sell
      ? tryPostSell(c, me, key, amount, price)
      : tryPostBuy(c, me, key, amount, price);
  if (!o) return;
  const each = (o.price / 10).toFixed(1);
  logLine(
    c,
    kind === OrderKind.Sell
      ? `${o.amount} ${RESOURCE_LABELS[key]} go on the book at ${each} gold each.`
      : `A bid goes on the book: ${o.amount} ${RESOURCE_LABELS[key]} wanted at ${each} gold.`,
  );
}

function doCancelOrder(c: CampaignState, me: number, orderId: number): void {
  const o = findOrder(c, orderId);
  if (!o || !cancelOrder(c, me, orderId)) return;
  logLine(
    c,
    o.kind === OrderKind.Sell
      ? `Our ${o.amount} ${RESOURCE_LABELS[o.key]} come off the book.`
      : `Our bid for ${RESOURCE_LABELS[o.key]} is withdrawn.`,
  );
}

// ── Holdings ───────────────────────────────────────────────────

/**
 * Put masons on a holding.
 *
 * The whole of the rule set lives in `developBlock`, which returns a *sentence*
 * rather than a boolean, and the world map is expected to have greyed the button
 * with that sentence on it already. This is the second line of defence, and it
 * refuses politely like everything else here: the timber may have been spent on
 * something else between the click and the tick.
 */
function doDevelop(c: CampaignState, me: number, province: number): void {
  if (province < 0 || province >= c.provinces.length) return;
  const cost = developCost(c, province);
  if (!cost) return;
  if (!beginWorks(c, province, me)) return;
  logLine(
    c,
    `Masons go to ${provinceName(province)} — ${c.provinces[province].holdingKind === 'village' ? `village level ${cost.to}` : tierName(cost.to).toLowerCase()} in ` +
      `${cost.months} months, out of the yard at ${provinceName(cost.route.city)}.`,
  );
}

function doCancelWorks(c: CampaignState, me: number, province: number): void {
  if (province < 0 || province >= c.provinces.length) return;
  if (!cancelWorks(c, province, me)) return;
  logLine(c, `The works at ${provinceName(province)} are called off. Half of it comes back.`);
}

// ── The sea ────────────────────────────────────────────────────────────────
//
// Every one of these refuses politely, like everything else in this file. The
// world map is expected to grey the button using the matching `…Refusal`
// function in `fleets.ts`, which returns a sentence saying what is wrong; these
// are the second line of defence rather than the first.

function doBuildPort(c: CampaignState, me: number, province: number): void {
  if (!tryBuildPort(c, me, province)) return;
  const level = c.provinces[province].port;
  logLine(
    c,
    level === 1
      ? `${provinceName(province)} opens a harbour. ${nationName(c, me)} looks seaward.`
      : `${provinceName(province)} extends its wharves to ${level}.`,
  );
}

function doRaiseFleet(
  c: CampaignState,
  me: number,
  province: number,
  ships: number,
  into: number,
): void {
  // Fleet ids count from 1, so 0 means "a new one" without needing a sentinel.
  if (into > 0) {
    const existing = findFleet(c, into);
    const added = tryAddHulls(c, me, into, ships);
    if (added <= 0 || !existing) return;
    logLine(c, `${added} more hulls join ${existing.name} at ${provinceName(existing.at)}.`);
    return;
  }
  const f = tryRaiseFleet(c, me, province, ships);
  if (!f) return;
  logLine(c, `${f.name} takes the water at ${provinceName(province)} — ${f.ships} hulls.`);
}

function doSail(c: CampaignState, me: number, fleetId: number, to: number): void {
  const f = findFleet(c, fleetId);
  if (!f || f.owner !== me) return;
  const months = sailMonths(c, f, to);
  if (!orderSail(c, f, to)) return;
  logLine(
    c,
    `${f.name} stands out for ${provinceName(to)} — ${months} ${months === 1 ? 'month' : 'months'} under sail.`,
  );
}

function doEmbark(c: CampaignState, me: number, fleetId: number, armyId: number): void {
  const f = findFleet(c, fleetId);
  const a = findArmy(c, armyId);
  if (!f || !a || f.owner !== me || a.owner !== me) return;
  if (!embarkArmy(c, f, a)) return;
  logLine(c, `${a.name} goes aboard ${f.name} at ${provinceName(f.at)}.`);
}

function doFleetAction(c: CampaignState, me: number, fleetId: number, action: number): void {
  const f = findFleet(c, fleetId);
  if (!f || f.owner !== me) return;
  switch (action) {
    case FleetAction.Disembark: {
      const where = f.at;
      const landed = disembarkFleet(c, f);
      if (landed <= 0) return;
      logLine(
        c,
        `${landed === 1 ? 'A host comes' : `${landed} hosts come`} ashore at ` +
          `${provinceName(where)} off ${f.name}.`,
      );
      return;
    }
    case FleetAction.Disband: {
      const name = f.name;
      const where = f.at;
      if (!disbandFleet(c, f)) return;
      logLine(c, `${name} is paid off at ${provinceName(where)}.`);
      return;
    }
    case FleetAction.Halt: {
      const home = f.moveFrom;
      if (!haltVoyage(f)) return;
      logLine(c, `${f.name} puts about for ${provinceName(home)}.`);
      return;
    }
    default:
      return;
  }
}

function doFleetIntercept(c: CampaignState, me: number, chaserId: number, targetId: number): void {
  const chaser = findFleet(c, chaserId);
  const target = findFleet(c, targetId);
  if (!chaser || !target) return;
  if (chaser.owner !== me) return;
  if (!orderFleetIntercept(c, chaser, target)) return;
  logLine(
    c,
    `${chaser.name} goes out after ${target.name} in the water off ` +
      `${provinceName(target.moveTo)}.`,
  );
}

function doReinforce(
  w: World,
  c: CampaignState,
  me: number,
  armyId: number,
  points: number,
): void {
  const a = findArmy(c, armyId);
  if (!a || a.owner !== me) return;
  const models = tryReinforceArmy(c, w.rngCampaign, me, armyId, points);
  if (models > 0) logLine(c, `${models} more join the ${a.name} at ${provinceName(a.at)}.`);
}
